import React, { useEffect, useState } from 'react';
import './QrCodeModal.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { apiFetch } from '../services/api';
import {Device, ProvisionData} from './admin/DeviceManagementPage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';
import LoadingIcon from "./LoadingIcon";

interface QrCodeModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description: string;
    qrDataUrl: string | null;
    setQrDataUrl?: (url: string | null) => void;
    isLoading: boolean;
    error: string | null;
    device?: Device;
    provisioningData?: ProvisionData;
    onProvisioned?: () => void;
}

// Provisioning requests expire after 2 minutes (DynamoDB TTL). A missing
// request before then means a device consumed it; after, it just expired.
const PROVISION_TTL_MS = 110 * 1000;

const QrCodeModal: React.FC<QrCodeModalProps> = ({ open, onClose, title, description, qrDataUrl, setQrDataUrl, isLoading, error, device, provisioningData, onProvisioned }) => {
    const [isProvisioned, setIsProvisioned] = useState(false);
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        if (open) {
            setIsProvisioned(false);
            setIsExpired(false);
            if (provisioningData && onProvisioned) {
                const startedAt = Date.now();
                const interval = setInterval(async () => {
                    try {
                        const response = await apiFetch(`/api/risc/device/provision/${provisioningData.id}`);
                        if (!response.ok) {
                            clearInterval(interval);
                            if (Date.now() - startedAt > PROVISION_TTL_MS) {
                                setIsExpired(true);
                                return;
                            }
                            setIsProvisioned(true);
                            setTimeout(() => {
                                if (onProvisioned) {
                                    onProvisioned();
                                }
                            }, 2000);
                        }
                    } catch (e) {
                        // 404 is expected until provisioned
                    }
                }, 2000);

                return () => clearInterval(interval);
            }
        }
    }, [open, provisioningData, onProvisioned]);

    const handleClose = () => {
        if (setQrDataUrl) {
            setQrDataUrl(null);
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ position: 'relative' }}>
                <Box sx={{ filter: (isProvisioned || isExpired) ? 'blur(4px) brightness(0.4)' : 'none', pointerEvents: (isProvisioned || isExpired) ? 'none' : 'auto' }}>
                    <Typography gutterBottom>{description}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>

                        {isLoading && <LoadingIcon isLoading={isLoading} showOverlay={false}/>}
                        {error && <Typography color="error">{error}</Typography>}
                        {qrDataUrl && <img src={qrDataUrl} alt="QR Code" style={{ maxWidth: '100%', height: 'auto' }} />}
                    </Box>
                </Box>
                {isExpired && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        color: 'warning.main'
                    }}>
                        <Typography variant="h6">QR Code Expired</Typography>
                        <Typography variant="body2">Close and generate a new one.</Typography>
                    </Box>
                )}
                {isProvisioned && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        color: 'success.main'
                    }}>
                        <FontAwesomeIcon icon={faCircleCheck} size="3x" />
                        <Typography variant="h6">Device Provisioned</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default QrCodeModal;
